import { z } from 'zod';
import { LIMITS, MESSAGE_TYPES, type MessageType } from '../constants.js';
import type { AttachmentDto } from './media.js';
import type { PollDto } from './poll.js';
import type { CalendarEventDto } from './calendar.js';
import type { GameSessionDto } from './game.js';
import type { StickerDto } from './sticker.js';

export interface ReactionDto {
  emoji: string;
  userIds: string[];
}

/**
 * Structured extras for non-text messages. Everything is optional so that
 * additional message types introduced by future modules stay backwards
 * compatible with older clients (they fall back to rendering `body`).
 */
export interface MessageMetadata {
  stickerId?: string;
  pollId?: string;
  eventId?: string;
  /**
   * Eine Termin-Karte, die der Erinnerungsdienst gesendet hat; `nummer` zählt je Person.
   * `art: 'vorab'` ist die „Erinnerung vor dem Termin“ (nummer 1, `minuten` vor dem
   * Beginn); ohne `art` ist es die Erinnerung an eine ausstehende Antwort.
   */
  erinnerung?: { nummer: number; art?: 'vorab'; minuten?: number };
  gameSessionId?: string;
  forwardedFrom?: { conversationId: string; messageId: string };
  /** System messages: machine readable event describing what happened. */
  system?: { kind: string; actorId?: string; targetIds?: string[]; value?: string };
  /** Free-form space reserved for feature modules. Never dropped by the server. */
  module?: Record<string, unknown>;
}

export interface MessageDto {
  id: string;
  conversationId: string;
  senderId: string | null;
  type: MessageType;
  body: string | null;
  attachments: AttachmentDto[];
  replyToId: string | null;
  replyTo: MessageSnippet | null;
  metadata: MessageMetadata;
  reactions: ReactionDto[];
  clientId: string | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  /** Expanded entities so a chat renders in a single round trip. */
  poll?: PollDto;
  event?: CalendarEventDto;
  game?: GameSessionDto;
  sticker?: StickerDto;
}

export interface MessageSnippet {
  id: string;
  senderId: string | null;
  type: MessageType;
  body: string | null;
  attachmentKind: string | null;
  deletedAt: string | null;
}

export const messageMetadataSchema: z.ZodType<MessageMetadata> = z.object({
  stickerId: z.string().uuid().optional(),
  pollId: z.string().uuid().optional(),
  eventId: z.string().uuid().optional(),
  erinnerung: z
    .object({
      nummer: z.number().int().min(1).max(100),
      art: z.literal('vorab').optional(),
      minuten: z.number().int().min(1).max(60 * 24 * 14).optional(),
    })
    .optional(),
  gameSessionId: z.string().uuid().optional(),
  forwardedFrom: z
    .object({ conversationId: z.string().uuid(), messageId: z.string().uuid() })
    .optional(),
  system: z
    .object({
      kind: z.string().max(64),
      actorId: z.string().uuid().optional(),
      targetIds: z.array(z.string().uuid()).max(100).optional(),
      value: z.string().max(500).optional(),
    })
    .optional(),
  module: z.record(z.unknown()).optional(),
});

/**
 * Was ein Client beim Senden in `metadata` mitgeben darf – eine Positivliste.
 *
 * Alles andere setzt der Server selbst: `system` (Systemzeilen), `eventId` und
 * `erinnerung` (Termin-Karten), `pollId`, `gameSessionId`. Nähme er sie vom
 * Client an, ließe sich etwa eine Systemzeile im Namen eines anderen fälschen.
 * Unbekannte Schlüssel lehnt der Server ab (`modules/messages.rs`).
 */
export type ClientMessageMetadata = Pick<MessageMetadata, 'stickerId' | 'forwardedFrom' | 'module'>;

export const clientMessageMetadataSchema: z.ZodType<ClientMessageMetadata> = z
  .object({
    stickerId: z.string().uuid().optional(),
    forwardedFrom: z
      .object({ conversationId: z.string().uuid(), messageId: z.string().uuid() })
      .strict()
      .optional(),
    module: z.record(z.unknown()).optional(),
  })
  .strict()
  .refine((wert) => JSON.stringify(wert).length <= LIMITS.messageMetadataMax, {
    message: 'Zusatzangaben sind zu groß',
  });

export const sendMessageSchema = z
  .object({
    type: z.enum(MESSAGE_TYPES).default('text'),
    body: z.string().max(LIMITS.messageBodyMax).nullable().optional(),
    attachmentIds: z.array(z.string().uuid()).max(LIMITS.attachmentsPerMessage).optional(),
    replyToId: z.string().uuid().nullable().optional(),
    /** Idempotency key generated by the client; re-sends return the same message. */
    // Ohne Doppelpunkt: Der gehört den Schlüsseln, die der Server vergibt.
    clientId: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[^:]+$/)
      .optional(),
    metadata: clientMessageMetadataSchema.optional(),
  })
  .refine(
    (v) =>
      (v.body != null && v.body.trim().length > 0) ||
      (v.attachmentIds?.length ?? 0) > 0 ||
      v.metadata?.stickerId != null,
    { message: 'message needs a body, an attachment or a referenced entity' },
  );
export type SendMessageInput = z.infer<typeof sendMessageSchema>;

export const editMessageSchema = z.object({
  body: z.string().min(1).max(LIMITS.messageBodyMax),
});

export const reactionSchema = z.object({
  emoji: z.string().min(1).max(16),
});

export const listMessagesSchema = z.object({
  limit: z.coerce.number().int().min(1).max(LIMITS.messagePageSizeMax).optional(),
  before: z.string().uuid().optional(),
  after: z.string().uuid().optional(),
});

export const searchMessagesSchema = z.object({
  q: z.string().trim().min(2).max(200),
  conversationId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
